import { Route, Routes } from 'react-router-dom'
import { LocaleProvider, postSeo } from '@kkf/design'
import { NotFoundScreen } from './components/NotFoundScreen'
import { SiteShell } from './components/SiteShell'
import { POSTS } from './data/blog'
import { ROUTES, SITE } from './data/seo'
import { ENGLISH, LOADERS } from './i18n'
import { HomePage } from './pages/HomePage'
import { ToursPage } from './pages/ToursPage'
import { TourDetailsPage } from './pages/TourDetailsPage'
import { ServicesPage } from './pages/ServicesPage'
import { ProgrammesPage } from './pages/ProgrammesPage'
import { ContactPage } from './pages/ContactPage'
import { BlogPage } from './pages/BlogPage'
import { BlogDetailsPage } from './pages/BlogDetailsPage'
import { PrivacyPage, TermsPage } from './pages/LegalPages'

/**
 * Route table — ten routes plus the catch-all.
 *
 * **The shop is gone (2026-09-19).** `/shop`, `/shop/:slug`, the two products and the cart
 * that added them up all came off at the Foundation's request: the gear is sold by a
 * partner, Radical RZ, and the nav entry is now `Partner Products` pointing at
 * `radicalrz.com/shop` in a new tab. A second checkout for somebody else's stock is not a
 * thing this workshop should be maintaining.
 *
 * `CartProvider` used to wrap this whole table — the cart had to survive navigation — and
 * `CartPanel` was a sibling of the routes because the drawer was shell, not page. Both are
 * deleted; `git log -- app_garage/src/cart` has them if a shop ever comes back.
 *
 * `SEO` is the site tree the shell applies on every navigation, and the same list the build
 * writes into per-route HTML. Module-level: `AppShell` memoises its lookup on this identity.
 */
const SEO = { site: SITE, routes: [...ROUTES, ...POSTS.map(postSeo)] }

/**
 * `LocaleProvider` is inside `App` and not outside it, for the reason it is in every other
 * app: the route suite renders `<App/>` directly, and a provider outside it would leave
 * every test throwing from `useLocale()`.
 */
export function App() {
  return (
    <LocaleProvider english={ENGLISH} loaders={LOADERS}>
      <Routes>
        <Route element={<SiteShell seo={SEO} />}>
          <Route path="/" element={<HomePage />} />
          {/* The bookable side. `/tours/:slug` renders the 404 for an unknown slug rather
              than an empty page — a tour page with no tour on it looks like a cancellation. */}
          <Route path="/tours" element={<ToursPage />} />
          <Route path="/tours/:slug" element={<TourDetailsPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/programmes" element={<ProgrammesPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/*
            The blog — one library shared with the other three public sites.

            The posts are markdown at the repository root and this app takes the ones tagged
            for it; see `data/blog` and `vite.config.ts`. `postSeo` describes each of them
            the same way the build does, so a post's head is the same whether a crawler or a
            visitor asks for it.
          */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailsPage />} />

          {/* The two shared legal pages. One component, one source of words — see
              `@kkf/design`'s `config/legal.ts`. */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
        </Route>

        {/* Outside the shell on purpose: the not-found page carries no header and no
            footer, so it must not be wrapped. It provides its own way back. */}
        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
    </LocaleProvider>
  )
}
